<script lang="ts" setup>
import { VContainer, VRow, VCol, VCard, VBtn, VIcon } from 'vuetify/components';
import DeliverableTable, {
    type DeliverableTableItem,
    type DeliverableTableSubItem,
} from '@/components/DeliverableTable.vue';
import { useQuery } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { computed, ref } from 'vue';
import { useRouteParams, useRouteQuery } from '@vueuse/router';
import { useI18n } from 'vue-i18n';
import DeliverableCreateModal from './DeliverableCreateModal.vue';
import PhaseCreateModal from './PhaseCreateModal.vue';
import { usePermissions } from '@/composables/permissions';
import { PolicyAction } from '@/types';
import type { Deliverable, Project } from '@/gql/graphql';
import { FormName, useFormSession } from '@/composables/form-session';
import DeliverableLinkModal from './DeliverableLinkModal.vue';
import { VMenu, VList, VListItem } from 'vuetify/components';

const { t } = useI18n({
    messages: {
        nl: {
            title: '@:deliverables',
            create_phase: 'Fase aanmaken',
            create_deliverable: '@:deliverable aanmaken',
            link_deliverable: '@:deliverable koppelen',
        },

        en: {
            title: '@:deliverables',
            create_phase: 'Create phase',
            create_deliverable: 'Create @.lower:deliverables',
            link_deliverable: 'Link @.lower:deliverable',
        },
    },
});

const { hasProjectPermission } = usePermissions();
const formSession = useFormSession();

const projectId = useRouteParams<string>('id');
const formQuery = useRouteQuery<FormName>('form');

const isShowingDeliverableCreateModal = ref(false);
const isShowingPhaseCreateModal = ref(false);
const isShowingDeliverableLinkModal = ref(false);

formSession.onResult(() => {
    if (formQuery.value && formSession.session.value?.fields) {
        switch (formQuery.value) {
            case FormName.deliverable_create:
                isShowingDeliverableCreateModal.value = true;
                break;
            case FormName.phase_create:
                isShowingPhaseCreateModal.value = true;
                break;
        }
    }
});

const variables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: projectId.value,
        },
    ],
}));

const { result, loading, error, refetch } = useQuery(
    graphql(`
        query ProjectPhases($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    id
                    masterPortfolioId
                    parentProjectId
                    subProjects {
                        id
                    }
                    deliverables {
                        id
                        name
                        status
                        state
                        milestone
                        startDate
                        endDate
                        linkedProjectId
                        phase {
                            id
                        }
                        project {
                            id
                            parentProjectId
                            masterPortfolioId
                            name
                        }
                    }

                    phases {
                        id
                        name
                        description
                        startDate
                        endDate
                        deliverables {
                            id
                        }
                    }
                }
            }
        }
    `),
    variables
);

const project = computed<Project>(() => result.value?.projects?.items?.[0]);

const hasSubProjects = computed(() => project.value.subProjects.length || 0);
const isSubproject = computed(() => project.value.parentProjectId);

const canUpdateProject = computed(() => {
    if (!project.value) return false;

    return (
        project.value &&
        (hasProjectPermission(PolicyAction.DELIVERABLE_WRITE, project.value) ||
            hasProjectPermission(PolicyAction.PROJECT_WRITE, project.value))
    );
});
const canEscalateDeliverable = computed(() => {
    if (!project.value) return false;

    return (
        project.value &&
        hasProjectPermission(PolicyAction.ESCALATION_CREATE, project.value)
    );
});
const phases = computed<DeliverableTableItem[]>(() => {
    type PhaseResult = NonNullable<typeof phases>[0];
    const phases = project.value?.phases;

    if (!phases?.length) return [];

    function deliverablesInPhase(phase: PhaseResult) {
        return (
            project.value?.deliverables
                ?.filter((deliverable) => deliverable.phase?.id === phase.id)
                .filter((deliverable) => deliverable.project !== null)
                .map((deliverable) => {
                    return {
                        id: deliverable.id,
                        name: deliverable.name,
                        type: {
                            isMilestone: deliverable.milestone,
                            status: deliverable.status,
                        },
                        state: deliverable.state,
                        startDate: deliverable.startDate ?? undefined,
                        endDate: deliverable.endDate,
                        linkedProjectId: deliverable.linkedProjectId,
                        project: {
                            id: deliverable.project!.id,
                            masterPortfolioId:
                                deliverable.project!.masterPortfolioId,
                            parentProjectId:
                                deliverable.project!.parentProjectId,
                            name: deliverable.project!.name,
                        },
                    };
                }) || []
        );
    }

    return phases.map((phase) => ({
        name: phase.name,
        id: phase.id,
        ...(phase.description && { description: phase.description }),
        ...(phase.startDate && { startDate: phase.startDate }),
        endDate: phase.endDate,
        deliverables: deliverablesInPhase(phase),
        project: {
            id: project.value?.id,
            masterPortfolioId: project.value?.masterPortfolioId,
            parentProjectId: project.value?.parentProjectId,
            name: project.value!.name,
        },
    }));
});

const unphasedDeliverables = computed<DeliverableTableItem[]>(() => {
    return (
        project.value?.deliverables
            ?.filter((deliverable) => !deliverable.phase)
            .filter((deliverable) =>
                isSubproject.value ? true : !deliverable.linkedProjectId
            )
            .filter((deliverable) => deliverable.project !== null)
            .map((deliverable) => deliverableToTableItem(deliverable, true)) ??
        []
    );
});

const linkedDeliverables = computed(() => {
    if (!project.value?.deliverables.length) return [];

    return project.value.deliverables
        .filter(
            (deliverable) =>
                deliverable.linkedProjectId !== null &&
                deliverable.linkedProjectId === project.value.id
        )
        .map((deliverable) => deliverableToTableItem(deliverable, true))
        .filter(Boolean);
});

const deliverableTableItems = computed<
    (DeliverableTableItem | DeliverableTableSubItem)[]
>(() => {
    return [
        ...phases.value,
        ...unphasedDeliverables.value,
        ...linkedDeliverables.value,
    ];
});

function deliverableToTableItem(
    deliverable: Deliverable,
    subItem?: true
): DeliverableTableSubItem;

function deliverableToTableItem(
    deliverable: Deliverable,
    subItem?: false
): DeliverableTableItem;

function deliverableToTableItem(
    deliverable: Deliverable,
    subItem?: boolean
): DeliverableTableItem | DeliverableTableSubItem {
    const isSubItem = subItem ?? false;

    const item: DeliverableTableItem = {
        id: deliverable.id,
        name: deliverable.name,
        startDate: deliverable.startDate ?? undefined,
        endDate: deliverable.endDate,
        linkedProjectId: deliverable.linkedProjectId,
        project: {
            id: deliverable.project?.id || '',
            masterPortfolioId: deliverable.project?.masterPortfolioId || '',
            parentProjectId: deliverable.project?.parentProjectId || '',
            name: deliverable.project?.name || '',
        },
    };

    if (isSubItem) {
        const subItem: DeliverableTableSubItem = {
            ...item,
            type: {
                isMilestone: deliverable.milestone,
                status: deliverable.status,
            },
            state: deliverable.state,
        };

        return subItem;
    }

    const result: DeliverableTableItem = {
        ...item,
    };

    return result;
}
</script>

<template>
    <v-container>
        <v-row>
            <v-col md="6" class="d-flex align-center">
                <h1
                    class="text-title-large text-primary-30 d-flex align-center ga-2"
                >
                    <v-icon class="text-primary-20">$escalation</v-icon>
                    {{ t('title') }}
                </h1>
            </v-col>
            <v-col md="6" align="right" class="d-flex ga-3 justify-end">
                <PhaseCreateModal
                    v-if="canUpdateProject"
                    v-model="isShowingPhaseCreateModal"
                    :project-id
                >
                    <template #activator="{ props }">
                        <v-btn
                            data-name="create-phase"
                            variant="outlined"
                            prepend-icon="$add"
                            v-bind="props"
                            >{{ t('create_phase') }}</v-btn
                        >
                    </template>
                </PhaseCreateModal>

                <DeliverableCreateModal
                    v-if="canUpdateProject"
                    v-model="isShowingDeliverableCreateModal"
                >
                    <template #activator="{ props }">
                        <v-btn
                            data-name="create-deliverable"
                            variant="outlined"
                            prepend-icon="$add"
                            :disabled="loading"
                            v-bind="props"
                        >
                            {{ t('create_deliverable') }}
                        </v-btn>
                    </template>
                </DeliverableCreateModal>

                <DeliverableLinkModal
                    v-model="isShowingDeliverableLinkModal"
                    :project-id
                >
                </DeliverableLinkModal>

                <VMenu
                    v-if="canUpdateProject && !isSubproject && hasSubProjects"
                >
                    <template #activator="{ props: activatorProps }">
                        <VBtnIcon
                            icon="$more"
                            variant="outlined"
                            color="primary"
                            v-bind="activatorProps"
                        ></VBtnIcon>
                    </template>
                    <VList>
                        <VListItem
                            :disabled="!hasSubProjects"
                            :title="t('link_deliverable')"
                            prepend-icon="$add_link"
                            @click="
                                () => (isShowingDeliverableLinkModal = true)
                            "
                        />
                    </VList>
                </VMenu>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-card class="pa-8">
                    <p v-if="error" class="text-error">{{ error }}</p>
                    <DeliverableTable
                        :current-project-id="projectId"
                        :items="deliverableTableItems"
                        :can-update="canUpdateProject"
                        :can-escalate="canEscalateDeliverable"
                        :loading
                        @phase-deleted="refetch"
                    />
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>
